import { useEffect, useState } from 'react';

/**
 * A `now` that advances on an interval while the tab is visible, and is brought up to date the moment it
 * becomes visible again.
 *
 * Held still while hidden because advancing it costs a fetch: `DateRangeContext` derives a live `endDate`
 * from this, and every query keyed on that range mints a fresh cache entry when it moves. A key change is an
 * initial fetch, which react-query does not focus-gate the way it gates `refetchInterval`.
 */
export const useNow = (updateIntervalSeconds: number, { enabled = true }: { enabled?: boolean } = {}) => {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let interval: ReturnType<typeof setInterval> | undefined;

        const start = () => {
            interval ??= setInterval(() => setNow(new Date()), updateIntervalSeconds * 1000);
        };

        const stop = () => {
            clearInterval(interval);
            interval = undefined;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                stop();
                return;
            }
            // stale by however long the tab was hidden, so it is caught up before the interval resumes
            setNow(new Date());
            start();
        };

        if (document.visibilityState !== 'hidden') {
            start();
        }
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stop();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [updateIntervalSeconds, enabled]);

    return now;
};
